import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { Loader2, User } from 'lucide-react';
import clsx from 'clsx';

interface ProfileStepProps {
  onComplete: () => void;
}

const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

export default function ProfileStep({ onComplete }: ProfileStepProps) {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user]);

  const saveStep = useMutation({
    mutationFn: async () => {
      return api.patch('/onboarding/step/2', {
        name,
        dateOfBirth,
        gender,
      });
    },
    onSuccess: () => {
      onComplete();
    },
  });

  const handleSubmit = () => {
    if (!name || !dateOfBirth || !gender) return;
    saveStep.mutate();
  };

  const isValid = name.length > 0 && dateOfBirth && gender;

  return (
    <div className="space-y-6">
      {/* Avatar Preview */}
      <div className="flex justify-center mb-8">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center">
          {name ? (
            <span className="text-3xl font-bold text-white">
              {name.charAt(0).toUpperCase()}
            </span>
          ) : (
            <User className="w-10 h-10 text-white" />
          )}
        </div>
      </div>

      {/* Name */}
      <div>
        <label htmlFor="name" className="label">
          Your Name *
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter your full name"
          className="input"
        />
      </div>

      {/* Date of Birth */}
      <div>
        <label htmlFor="dob" className="label">
          Date of Birth *
        </label>
        <input
          id="dob"
          type="date"
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
          className="input"
          max={new Date().toISOString().split('T')[0]}
        />
        <p className="text-xs text-gray-500 mt-1">
          Used to match you with people from the same era
        </p>
      </div>

      {/* Gender */}
      <div>
        <label className="label">Gender *</label>
        <div className="grid grid-cols-2 gap-2">
          {GENDERS.map((g) => (
            <button
              key={g.value}
              type="button"
              onClick={() => setGender(g.value)}
              className={clsx(
                'p-3 rounded-lg border-2 text-sm font-medium transition-all',
                gender === g.value
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300'
              )}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {/* Submit */}
      <div className="pt-4">
        <button
          onClick={handleSubmit}
          disabled={!isValid || saveStep.isPending}
          className="btn-primary w-full py-3 disabled:opacity-50"
        >
          {saveStep.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            'Continue'
          )}
        </button>
        {saveStep.isError && (
          <p className="text-sm text-red-600 mt-2 text-center">
            Failed to save. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}
